import {
  ANALYTICS_EVENT_NAMES,
  ANALYTICS_PLANNED_EVENTS,
  type AnalyticsEventName,
} from "@lafayette/schemas";

// The events in plain words (ADR 0017, decision 9; CHT-2030). ONE template for each event name, written here from CLOSED values only: the label of an
// enum, a bucket or a flag. A value that a template does not know is written as "something else", never copied, and a free text cannot reach a
// sentence because no template reads a key that the taxonomy does not list. No route sends a sentence: the server sends names and closed values.
// A test fails when an event name has no template.

type Props = Readonly<Record<string, string | number | boolean>>;

/** A phrase: `customer` phrases start with a verb ("opened the app") and get the name in front; the others are whole clauses. */
type Phrase = Readonly<{ text: string; customer: boolean }>;
type Template = (props: Props) => Phrase;

const customer = (text: string): Phrase => ({ text, customer: true });
const system = (text: string): Phrase => ({ text, customer: false });

const STRANGE = "something else";
/** The label of a closed value, or "something else": an unknown value is never written out. */
function label(words: Readonly<Record<string, string>>, value: unknown): string {
  return typeof value === "string" && Object.hasOwn(words, value)
    ? (words[value] ?? STRANGE)
    : STRANGE;
}

const ENTRY: Readonly<Record<string, string>> = {
  cold_start: "from a closed app",
  resume: "again after leaving it for a while",
  deep_link: "from a link",
  notification: "from a notification",
};
const SCREEN: Readonly<Record<string, string>> = {
  concierge: "the chat",
  trips: "Trips",
  trip_detail: "a trip",
  trip_new: "a new trip",
  trip_edit: "a trip to change",
  itinerary_add: "the page to add to a plan",
  itinerary_review: "the plan review",
  itinerary_edit: "the plan editor",
  actions: "Action Center",
  action_detail: "an action",
  saved: "Saved",
  saved_item: "a saved item",
  profile: "Profile",
  memory: "Memory",
  request_new: "a new request",
  request_answer: "a question from the concierge",
  sign_in: "Sign in",
  other: "another screen",
};
const PLATFORM: Readonly<Record<string, string>> = {
  ios: "an iPhone or iPad",
  android: "an Android phone",
};
const END_REASON: Readonly<Record<string, string>> = {
  background: "left the app",
  idle_timeout: "stopped using the app (30 minutes with no activity)",
  sign_out: "signed out",
};
const DURATION: Readonly<Record<string, string>> = {
  lt_1m: "under 1 minute",
  "1m_to_5m": "1 to 5 minutes",
  "5m_to_30m": "5 to 30 minutes",
  "30m_to_4h": "30 minutes to 4 hours",
  gte_4h: "over 4 hours",
};
const COUNT: Readonly<Record<string, string>> = {
  "0": "no",
  "1": "1",
  "2_to_3": "2 or 3",
  "4_to_9": "4 to 9",
  gte_10: "10 or more",
};
const AMOUNT: Readonly<Record<string, string>> = {
  lt_5k: "under ₹5,000",
  "5k_to_25k": "₹5,000 to ₹25,000",
  "25k_to_100k": "₹25,000 to ₹1,00,000",
  "100k_to_500k": "₹1,00,000 to ₹5,00,000",
  gte_500k: "over ₹5,00,000",
};
const SAVE_SOURCE: Readonly<Record<string, string>> = {
  chat: "in the chat",
  card: "from a suggestion card",
  itinerary: "from a plan",
  browse: "while browsing",
};
const REQUEST_SOURCE: Readonly<Record<string, string>> = {
  chat: "in the chat",
  card: "from a suggestion card",
  itinerary: "from a plan",
  staff: "with a concierge",
};
const CARD_KIND: Readonly<Record<string, string>> = {
  catalog_item: "a catalog suggestion",
  itinerary: "a plan suggestion",
  request_update: "a request update",
  action: "an action",
};
const TOPIC: Readonly<Record<string, string>> = {
  request_update: "a request update",
  handoff: "a message about a handoff",
  action_needed: "an action needed",
  trip_inspiration: "a trip inspiration",
  product_news: "product news",
};
const ACTIVATION: Readonly<Record<string, string>> = {
  profile_completed: "completed the profile",
  first_conversation: "had a first conversation",
  first_save: "saved something for the first time",
  first_request: "made a first request",
};
const RUN_OUTCOME: Readonly<Record<string, string>> = {
  COMPLETED: "The assistant answered a message.",
  DEGRADED: "The assistant answered a message with limits.",
  FAILED: "The assistant could not answer a message.",
};
const EDIT_KIND: Readonly<Record<string, string>> = {
  add_item: "An item was added to a plan",
  move_item: "An item of a plan was moved",
  remove_item: "An item was removed from a plan",
  retime_item: "An item of a plan was moved to another time",
  other: "A plan was changed",
};
const ORIGIN: Readonly<Record<string, string>> = {
  customer: "by the customer",
  staff: "by a concierge",
  agent: "by the assistant",
};
const HANDOFF_REASON: Readonly<Record<string, string>> = {
  CUSTOMER_REQUESTED_PERSON: "the customer asked for a person",
  SUPPLIER_CONTACT_REQUIRED: "a supplier has to be contacted",
  CONCIERGE_JUDGMENT_REQUIRED: "a concierge has to decide",
  CATALOG_DATA_CONFLICT: "the catalog data does not agree",
  UNSUPPORTED_HIGH_IMPACT_REQUEST: "the request is too big for the assistant",
  COMPLAINT_SERVICE_RECOVERY: "a complaint needs care",
  PAYMENT_FULFILMENT_EXCEPTION: "a payment needs a person",
  REPEATED_NO_PROGRESS: "the chat was not making progress",
  POLICY_REVIEW_REQUIRED: "a rule needs a person",
};
const HANDOFF_REQUESTER: Readonly<Record<string, string>> = {
  CUSTOMER: "by the customer",
  AI_POLICY: "by the assistant",
  STAFF: "by a concierge",
  SYSTEM: "by the system",
};
const ACTION_KIND: Readonly<Record<string, string>> = {
  answer_question: "answered a question",
  approve_quote: "approved a quote",
  confirm_itinerary: "confirmed a plan",
  other: "completed an action",
};
const OPERATION: Readonly<Record<string, string>> = {
  request_assigned: "assigned a request",
  handoff_accepted: "accepted a handoff",
  catalog_published: "published a catalog item",
  import_approved: "approved an import",
  memory_reviewed: "reviewed a memory",
  itinerary_validated: "validated a plan",
};
const PAYMENT_MODE: Readonly<Record<string, string>> = { fake: " (a test payment)", real: "" };
const STATE_WORDS: Readonly<Record<string, string>> = {
  DRAFT: "a draft",
  SUBMITTED: "submitted",
  ASSIGNED: "assigned to a concierge",
  IN_PROGRESS: "in progress",
  AWAITING_CUSTOMER: "waiting for the customer",
  PAYMENT_REQUIRED: "waiting for payment",
  CONFIRMED: "confirmed",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  UNAVAILABLE: "not available",
  DECLINED: "declined",
  EXPIRED: "expired",
};

const payment =
  (verb: string): Template =>
  (p) =>
    system(
      `${verb}${p.amount_bucket === undefined ? "" : ` (${label(AMOUNT, p.amount_bucket)})`}${label(PAYMENT_MODE, p.provider_mode) === STRANGE ? "" : label(PAYMENT_MODE, p.provider_mode)}`,
    );

/** The template of every event name. `satisfies` makes a missing name a compile error; the test makes it a failing test too. */
const TEMPLATES = {
  app_opened: (p) => customer(`opened the app ${label(ENTRY, p.entry)}`),
  session_started: (p) =>
    customer(`started a visit on ${label(PLATFORM, p.platform)}, ${label(ENTRY, p.entry)}`),
  screen_viewed: (p) =>
    customer(
      p.previous === "none" || p.previous === undefined
        ? `opened ${label(SCREEN, p.screen)}`
        : `went from ${label(SCREEN, p.previous)} to ${label(SCREEN, p.screen)}`,
    ),
  session_ended: (p) =>
    customer(
      `${label(END_REASON, p.end_reason)} after ${label(DURATION, p.duration_bucket)} and ${label(COUNT, p.screens_bucket)} ${p.screens_bucket === "1" ? "screen" : "screens"}`,
    ),
  activation_step_completed: (p) => customer(label(ACTIVATION, p.step)),
  concierge_run_completed: (p) => system(label(RUN_OUTCOME, p.outcome)),
  recommendation_viewed: (p) =>
    customer(
      `saw ${label(CARD_KIND, p.card_kind)}${p.position_bucket === "0" ? " at the top of the list" : ""}`,
    ),
  save_added: (p) =>
    customer(`saved an item${p.source === undefined ? "" : ` ${label(SAVE_SOURCE, p.source)}`}`),
  save_removed: () => customer("removed a saved item"),
  request_created: (p) =>
    customer(
      `opened a request${p.source === undefined ? "" : ` ${label(REQUEST_SOURCE, p.source)}`}${p.has_catalog_item === true ? " about a catalog item" : ""}`,
    ),
  request_state_changed: (p) => system(`A request is now ${label(STATE_WORDS, p.to_state)}`),
  itinerary_created: (p) => system(`A plan was started ${label(ORIGIN, p.origin)}`),
  itinerary_edited: (p) =>
    system(
      `${label(EDIT_KIND, p.edit_kind)}${p.item_count_bucket === undefined ? "" : ` (${label(COUNT, p.item_count_bucket)} ${p.item_count_bucket === "1" ? "item" : "items"})`}`,
    ),
  handoff_requested: (p) =>
    system(
      `A person was asked for ${label(HANDOFF_REQUESTER, p.requester)}: ${label(HANDOFF_REASON, p.reason)}`,
    ),
  handoff_accepted: (p) =>
    system(
      p.time_to_accept_bucket === undefined
        ? "A person took the handoff"
        : `A person took the handoff after ${label(DURATION, p.time_to_accept_bucket)}`,
    ),
  action_completed: (p) => customer(label(ACTION_KIND, p.action_kind)),
  notification_opened: (p) => customer(`opened ${label(TOPIC, p.topic)} notification`),
  admin_operation_performed: (p) => system(`A staff member ${label(OPERATION, p.operation)}`),
  experiment_exposed: () => system("The customer was shown a test variant of a screen"),
  payment_requested: payment("A payment request was created"),
  payment_started: payment("A payment was started"),
  payment_succeeded: payment("A payment succeeded"),
  payment_failed: payment("A payment failed"),
  payment_expired: payment("A payment request expired"),
  payment_refunded: payment("A payment was refunded"),
} as const satisfies Record<AnalyticsEventName, Template>;

const capital = (text: string) => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

/** The first word of a display name: the name the sentences use ("Anika Rao" becomes "Anika"). A name that is empty becomes "The customer". */
export function firstNameOf(displayName: string): string {
  const first = displayName.trim().split(/\s+/u)[0] ?? "";
  return first === "" ? "The customer" : first;
}

/** One line of a timeline or a card: no subject ("Opened the app from a notification"). */
export function stepSentence(event: AnalyticsEventName, props: Props): string {
  return capital(TEMPLATES[event](props).text);
}

/** The event in plain words with the name of the customer ("Anika opened the app from a notification."). */
export function plainWords(event: AnalyticsEventName, props: Props, name: string): string {
  const phrase = TEMPLATES[event](props);
  return phrase.customer ? `${name} ${phrase.text}.` : `${capital(phrase.text)}.`;
}

/** TRACKED: something in the product sends this event. PLANNED: it is in the taxonomy and nothing sends it yet. */
export function eventStatusOf(event: AnalyticsEventName): "TRACKED" | "PLANNED" {
  return (ANALYTICS_PLANNED_EVENTS as readonly string[]).includes(event) ? "PLANNED" : "TRACKED";
}

/** The event names that have a template, for the test that no event is left out. */
export const SENTENCE_EVENTS: readonly AnalyticsEventName[] = ANALYTICS_EVENT_NAMES;
